/**
 * The SignNow front-end event contract — MEP-1371.
 *
 * ME-rendered SignNow pages fired no funnel events: get-form read 19 against 11,204 on
 * desktop and an exact zero on mobile, `register` and `trial` were both zero. The
 * ticket framed this as missing coverage, but checking the names first — as it asked —
 * reframes it: the five events ME *did* push were named nothing GTM listens for.
 *
 * Both renders push to the same container (`GTM-TSK6FX7`), which forwards
 * `Event matches CE_.*` to GA4 plus triggers on the legacy names. ME was pushing
 * `registration`, `trial_purchased`, `free_trial_action`, `ce_signup`, `ce_login` —
 * none of which match anything, so all five were inert. Hence this module is a port of
 * the legacy contract rather than an extension of ME's own:
 *
 * | legacy event            | replaces ME's        |
 * | ----------------------- | -------------------- |
 * | `SNRegistration`        | `registration`       |
 * | `SNFreeTrial`           | `trial_purchased`    |
 * | `CustomEvt` (SN-Auth)   | `free_trial_action`  |
 * | `CE_signUp_submit`      | `ce_signup`          |
 * | `CE_login_submit`       | `ce_login`           |
 *
 * Ported from `SignNowInc/SignNowStaticRedesign`
 * `legacy/scripts/helpers/googleAnalyticsEvents.js`. Names, parameter shapes and the
 * hashing are copied exactly so existing GA/GTM reports read both arms without
 * per-render special-casing.
 *
 * Scope is the form-page contract only — the events those pages actually emit, via
 * `pages/form/index.js` and the `commonMain` bundle it imports. The rest of
 * `googleAnalyticsEvents.js` (`CE_sample_view`, `CE_tryNow_click`,
 * `CE_tryTemplate_click`, `CE_apiLeadform_submit`, `CE_document_upload`,
 * `CE_apiSandbox_*`, the doc-upload events) binds on other page types and is
 * deliberately out of scope.
 */

import { md5 } from './md5';

interface EventPair {
  ep_name: string;
  ep_value: string;
}

/**
 * Legacy does `window.dataLayer = window.dataLayer || []` before every push, and that
 * detail matters here: these listeners are installed from the library's global script,
 * which can run before the GTM snippet near the end of the body has created the array.
 * ME's previous `window.dataLayer?.push(...)` silently dropped anything pushed in that
 * window; GTM replays whatever it finds already in the array, so creating it preserves
 * the event instead.
 */
const push = (payload: Record<string, unknown>): void => {
  (window.dataLayer = window.dataLayer || []).push(payload as never);
};

/** Legacy sends the email twice: MD5-hashed inside `pairs`, and raw at the top level. */
const hashedEmailPair = (email: string): EventPair => ({ ep_name: 'hashedEmail', ep_value: md5(email) });

/** `SNRegistration` — a new account was created. */
export const onRegistration = (): void => push({ event: 'SNRegistration' });

/** `SNFreeTrial` — the new account came with a trial. */
export const onTrialPurchased = (): void => push({ event: 'SNFreeTrial' });

/**
 * `CustomEvt` in the `SN-Auth` category. Note the legacy argument order: the provider
 * becomes the label and the action becomes the action.
 */
export const onRegularFreeTrialActions = (provider: string, action: string): void =>
  push({
    event: 'CustomEvt',
    eventCategory: 'SN-Auth',
    eventAction: action,
    eventLabel: provider,
  });

/** `CE_signUp_submit` — One Tap produced a registration. */
export const onCESignUpSubmit = (email: string): void =>
  push({
    event: 'CE_signUp_submit',
    pairs: [{ ep_name: 'via', ep_value: 'one tap' }, hashedEmailPair(email)],
    email,
  });

/** `CE_login_submit` — One Tap matched an existing account. */
export const onCELoginSubmit = (email: string): void =>
  push({
    event: 'CE_login_submit',
    pairs: [{ ep_name: 'via', ep_value: 'one tap' }, hashedEmailPair(email)],
    email,
  });

/**
 * `CE_getForm_click` — the metric the rollout decision depends on. Legacy binds it to
 * `.button.js-editor-link` in `pages/form/index.js:176` and `gov.js:35`.
 */
export const onCEGetFormClick = (): void => push({ event: 'CE_getForm_click' });

/** `CE_leadform_click` — the footer "contact sales" routes. */
export const onCELeadformClick = (): void => push({ event: 'CE_leadform_click' });

/** `CE_freeTrial_click` — a call to action whose label offers a trial. */
export const onCeFreeTrialClick = (): void => push({ event: 'CE_freeTrial_click' });

/** `CE_freeTrial_submit` — the footer trial form was submitted. */
export const onCeFreeTrialSubmit = (email: string): void =>
  push({
    event: 'CE_freeTrial_submit',
    pairs: [hashedEmailPair(email)],
    email,
  });
